<template>
  <div class="py-6 options-container">
    <h2 class="text-grey-900 mb-6 options-title">
      {{ $t('project.checklist.dialogs.SmartImportOptions.title') }}
    </h2>
    
    <div class="d-flex options-list">
      <div
        v-for="(option, index) in options"
        :key="index"
        class="option-card pa-6 d-flex align-center flex-1-1-0"
        :class="{ 'selected': option.value === modelValue }"
        @click="selectOption(option.value)"
        :data-locator="option.locator"
      >
        <div class="d-flex align-center justify-center mr-4 option-icon" :class="option.iconBgClass">
          <app-material-icon
            :name="option.icon"
            size="md"
            type="fa"
            :color="option.iconColor"
          />
        </div>
        <div class="flex-grow-1">
          <p class="option-title mb-0">
            {{ $t(option.titleKey) }}
          </p>
          <p class="option-subtitle text-grey-700 mb-0">
            {{ $t(option.descriptionKey) }}
          </p>
        </div>
        <div v-if="option.value === modelValue" class="d-flex align-center justify-center">
          <app-material-icon
            name="circle-check"
            size="sm"
            type="fa"
            color="darkblue-500"
            weight="fas"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

type ImportMode = 'replace' | 'append'

export default defineComponent({
  name: 'SmartImportOptions',

  props: {
    modelValue: {
      type: String as () => ImportMode,
      default: 'replace',
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      options: [
        {
          value: 'replace' as ImportMode,
          icon: 'arrow-right-arrow-left',
          iconColor: 'green-500',
          iconBgClass: 'option-icon-replace',
          titleKey: 'project.checklist.dialogs.SmartImportOptions.replaceTitle',
          descriptionKey: 'project.checklist.dialogs.SmartImportOptions.replaceDescription',
          locator: 'replace-option',
        },
        {
          value: 'append' as ImportMode,
          icon: 'plus',
          iconColor: 'peach-500',
          iconBgClass: 'option-icon-append',
          titleKey: 'project.checklist.dialogs.SmartImportOptions.appendTitle',
          descriptionKey: 'project.checklist.dialogs.SmartImportOptions.appendDescription',
          locator: 'append-option',
        },
      ],
    }
  },
  methods: {
    selectOption(mode: ImportMode) {
      this.$emit('update:modelValue', mode)
    },
  },
})
</script>

<style lang="scss" scoped>
.options-container {
  min-height: 100%;
  border-bottom: 1px solid #e0e0e0;
  border-top: 1px solid #e0e0e0;
}

.options-title {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}

.options-list {
  gap: 16px;
  flex-direction: row;
}

.option-icon {
  min-width: 40px;
  height: 40px;
  border-radius: 8px;
  
  &.option-icon-replace {
    background-color: rgb(var(--v-theme-main-50));
  }
  
  &.option-icon-append {
    background-color: rgb(var(--v-theme-peach-50));
  }
}

.option-title {
  color: rgb(var(--v-theme-grey-900));
  font-weight: 600;
}
.option-title, .option-subtitle {
  line-height: 20px;
}

.option-card {
  border: 2px solid rgb(var(--v-theme-grey-100));
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &.selected {
    border-color: rgb(var(--v-theme-darkblue-500));
    background-color: rgb(var(--v-theme-darkblue-50)) !important;
  }
}

</style>
